import { Trans, useLingui } from "@lingui/react/macro";
import { Button, Center, Stack, Text, Title } from "@mantine/core";
import {
	IconArrowsSplit,
	IconBox,
	IconChefHat,
	IconCoins,
	IconHome2,
	IconSquareCheck,
} from "@tabler/icons-react";
import { useLocation } from "react-router-dom";
import { NewBadge } from "@/components/common/ui/badge/NewBadge";
import { routesList } from "@/routesList";
import { useAppNavigate } from "@/utils/hooks/useAppNavigate";
import { useAppSizeId } from "@/utils/hooks/useAppSizeId";
import { useHandleNavigate } from "@/utils/hooks/useHandleNavigate";

export const MODULE_APPS = [
	{
		icon: <IconCoins />,
		appName: "money",
		route: routesList.money.path,
		isNew: false,
	},
	{
		icon: <IconSquareCheck />,
		appName: "tasks",
		route: routesList.tasks.path,
		isNew: false,
	},
	{
		icon: <IconBox />,
		appName: "items",
		route: routesList.items.path,
		isNew: false,
	},
];

export const EXPERIMENT_APPS = [
	{
		icon: <IconArrowsSplit />,
		appName: "split",
		route: routesList.split.path,
		isNew: false,
	},
	{
		icon: <IconChefHat />,
		appName: "recipes",
		route: routesList.recipes.path,
		isNew: true,
	},
];

export const APPS = [...MODULE_APPS, ...EXPERIMENT_APPS];

/**
 * Hook que devuelve las etiquetas y descripciones traducibles de las apps.
 * Usa el macro `t` de Lingui para que `lingui extract` las encuentre.
 */
export function useAppLabels() {
	const { t } = useLingui();

	return {
		labels: {
			money: t`Dinero`,
			items: t`Despensa`,
			tasks: t`Tareas`,
			split: t`Gastos compartidos`,
			recipes: t`Recetas`,
		},
		descriptions: {
			money: t`Controla tus finanzas domésticas. Registra ingresos, gastos y visualiza hábitos de consumo.`,
			items: t`Gestiona tu despensa. Lleva inventario de productos y genera listas de compra automáticamente.`,
			tasks: t`Organiza tus tareas del hogar. Crea listas, establece prioridades y nunca olvides lo que necesitas hacer.`,
			split: t`Apunta quién paga qué entre varias personas y salda las deudas de una sola vez.`,
			recipes: t`Guarda tus recetas y tenlas siempre a mano cuando cocines.`,
		},
	};
}

/**
 * @param {object} props
 * @param {() => void} [props.closeMobileFnc]
 * @param {boolean} [props.withTitle=true]
 */
export default function AppSelector({ closeMobileFnc = () => {}, withTitle = true }) {
	const location = useLocation();
	const appNavigate = useAppNavigate();
	const { handleNavigate } = useHandleNavigate();

	const { labels } = useAppLabels();

	const { isMobile } = useAppSizeId();

	const currentApp = (location.pathname ?? "").split("/")[1]; // "/tasks/asd" -> "tasks"

	const onAppClick = (app) => {
		appNavigate(app.appName);

		if (isMobile) {
			closeMobileFnc();
		}
	};

	const renderApps = (apps) =>
		apps.map((_app) => {
			const isCurrentApp = _app.appName === currentApp;
			const isExactPath = location.pathname === _app.route;

			return (
				<Stack key={_app.appName} gap="0">
					<Button
						justify="flex-start"
						variant={isCurrentApp ? "light" : "subtle"}
						onClick={_app.route && !isExactPath ? () => onAppClick(_app) : undefined}
						leftSection={_app.icon}
					>
						<Text>{labels[_app.appName]}</Text>
					</Button>

					{_app.isNew && (
						<Center>
							<NewBadge ml="xxxs" style={{ position: "relative", top: -6 }} />
						</Center>
					)}
				</Stack>
			);
		});

	return (
		<Stack gap="sm">
			{withTitle && (
				<Title order={4}>
					<Trans>Módulos</Trans>
				</Title>
			)}

			<Button
				variant="subtle"
				color="green"
				leftSection={<IconHome2 />}
				justify="flex-start"
				onClick={() => {
					handleNavigate(routesList.dashboard.path);
				}}
			>
				<Text>
					<Trans>Inicio</Trans>
				</Text>
			</Button>

			{renderApps(MODULE_APPS)}

			{withTitle && (
				<Title order={4} mt="xs">
					<Trans>Experimentos</Trans>
				</Title>
			)}

			{renderApps(EXPERIMENT_APPS)}
		</Stack>
	);
}
